import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import ApiInspector from '../../../../components/ApiInspector';
import ComboFiltro from '../../../../components/ComboFiltro';
import { apiFetch } from '../../../../lib/api';
import { useFiltroSocieta } from '../../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../../context/PermessiContext';
import { showToast } from '../../../../components/Toast';
import { API_DOCS } from './ContrattiPage.api';
import './ContrattiPage.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoContratto = 'ATTIVO';
type Periodicita = 'MENSILE' | 'TRIMESTRALE' | 'SEMESTRALE' | 'ANNUALE';

interface Contratto {
  id: number;
  numero: string;
  stato: StatoContratto;
  clienteRagioneSociale: string | null;
  descrizione: string | null;
  dataInizio: string | null;
  dataFine: string | null;
  periodicita: Periodicita | null;
  modalitaPagamento: string | null;
  societaId: number;
  commessaId: number | null;
  commessaNumero: string | null;
  ordineId: number | null;
  ordineNumero: string | null;
  rigaOrdineOrigineId: number | null;
}

interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PERIODICITA_LABEL: Record<Periodicita, string> = {
  MENSILE: 'Mensile',
  TRIMESTRALE: 'Trimestrale',
  SEMESTRALE: 'Semestrale',
  ANNUALE: 'Annuale',
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

function renderPeriodo(c: Contratto): React.ReactNode {
  if (!c.dataInizio && !c.dataFine) return <span className="bop-text-muted">—</span>;
  return (
    <span className="contr-periodo">
      {formatDateIT(c.dataInizio)} <span className="bop-text-muted">→</span> {formatDateIT(c.dataFine)}
    </span>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function ContrattiPage() {
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { isSuperadmin } = usePermesso();
  const navigate = useNavigate();

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Filtri
  const [commessaId, setCommessaId] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati
  const [items, setItems] = useState<Contratto[]>([]);
  const [loading, setLoading] = useState(false);
  const [commesseOpts, setCommesseOpts] = useState<CommessaOption[]>([]);

  // Debounce ricerca testo
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Carica lista
  const caricaLista = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (commessaId) params.set('commessaId', commessaId);
      if (q) params.set('q', q);
      const qs = params.toString() ? `?${params.toString()}` : '';

      // @api GET /api/contratti?societaId&commessaId&q -> {"success":true,"data":[{"id":"number","numero":"string","stato":"string","clienteRagioneSociale":"string","descrizione":"string","dataInizio":"string","dataFine":"string","periodicita":"string","modalitaPagamento":"string","ordineNumero":"string","commessaNumero":"string"}]}
      const r = await apiFetch<{ success: boolean; data: Contratto[] }>(`/api/contratti${qs}`);
      setItems(r.data || []);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nel caricamento', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, commessaId, q]);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  // Carica commesse per il combo (cambia con societaId effettiva)
  useEffect(() => {
    if (!societaIdEff) {
      setCommesseOpts([]);
      return;
    }
    // @api GET /api/commesse?societaId={id} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?societaId=${societaIdEff}`)
      .then(r => setCommesseOpts(r.commesse || []))
      .catch(() => setCommesseOpts([]));
  }, [societaIdEff]);

  // Resetta commessa se cambia società
  useEffect(() => { setCommessaId(''); }, [societaIdEff]);

  const commesseComboItems = commesseOpts.map(c => ({
    id: String(c.id),
    nome: `${c.numero} — ${c.descrizione || ''}`.trim(),
  }));

  const pulisciFiltri = () => {
    setCommessaId('');
    setQInput('');
    setQ('');
  };

  return (
    <Layout>
      <div className="contr-page">
        {/* Header — sola lettura: i contratti nascono dalla contrattualizzazione delle righe ordine */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Contratti</h1>
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar contr-filters-row">
          {!isDisabled && (
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={societaIdLocale}
                onChange={e => setSocietaIdLocale(e.target.value)}
              >
                {isSuperadmin && <option value="">Tutte</option>}
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Cerca</label>
            <input
              className="bop-input contr-search-input"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder="Numero, cliente, descrizione..."
            />
          </div>

          <div className="bop-form-group contr-combo-group">
            <label className="bop-label">Commessa</label>
            <ComboFiltro
              label="commessa"
              value={commessaId || null}
              onChange={id => setCommessaId(id || '')}
              items={commesseComboItems}
              disabled={!societaIdEff}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">&nbsp;</label>
            <button className="bop-btn bop-btn-secondary" onClick={pulisciFiltri}>
              Pulisci filtri
            </button>
          </div>
        </div>

        {/* Tabella */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per visualizzare i contratti</div>
          </div>
        ) : (
          <div className="bop-card">
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Numero</th>
                  <th>Cliente</th>
                  <th>Descrizione</th>
                  <th>Periodo</th>
                  <th>Periodicità</th>
                  <th>Stato</th>
                  <th>Ordine / Commessa</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="bop-empty-state">Caricamento…</div>
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="bop-empty-state">Nessun contratto trovato con i filtri attuali.</div>
                    </td>
                  </tr>
                ) : (
                  items.map(c => (
                    <tr key={c.id} onClick={() => navigate(`/commerciale/attivo/contratti/${c.id}`)}>
                      <td><span className="contr-numero">{c.numero}</span></td>
                      <td>{c.clienteRagioneSociale || <span className="bop-text-muted">—</span>}</td>
                      <td>{c.descrizione || <span className="bop-text-muted">—</span>}</td>
                      <td>{renderPeriodo(c)}</td>
                      <td>
                        {c.periodicita
                          ? PERIODICITA_LABEL[c.periodicita]
                          : <span className="bop-text-muted">—</span>}
                      </td>
                      <td>
                        {c.stato === 'ATTIVO'
                          ? <span className="bop-badge bop-badge-success">Attivo</span>
                          : <span className="bop-badge bop-badge-neutral">{c.stato}</span>}
                      </td>
                      <td>
                        <div className="contr-ordine-commessa">
                          {c.ordineNumero && <span className="contr-ordine-numero">{c.ordineNumero}</span>}
                          {c.commessaNumero && <span className="bop-text-muted">{c.commessaNumero}</span>}
                          {!c.ordineNumero && !c.commessaNumero && <span className="bop-text-muted">—</span>}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
